import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {Link} from 'components/DSYS/Link';
import {formatShortDate} from 'helpers/dates';
import i18n from 'i18next';
import type {AiQueryToolProgress} from 'views/admin/adminAiQueryApi';

/**
 * Dash is a sources step, not a rows step: its evidence is which searches ran
 * ("Searching your Slack messages, Oct 19–25", then "20 messages") and which
 * messages and docs it cited, not a table echoing its prose answer.
 */

type DashSource = {title: string; url: string | null};

/** What the backend calls Dash's own search, as opposed to a connected app's. */
const DASH_SOURCE = 'Dash';

/** One line under a step: what it did, and on a Dash search what that found. */
export type ProgressLine = {progress: AiQueryToolProgress; found?: string};

/**
 * A Dash count line ("20 messages") reads as the end of its search line: the
 * one with its `call` id, since parallel searches return in any order. Older
 * servers send no ids, so their count goes on the search line just before it.
 */
export const progressLines = (progress: AiQueryToolProgress[]): ProgressLine[] =>
  progress.reduce<ProgressLine[]>((lines, p) => {
    if (p.found == null) {
      return [...lines, {progress: p}];
    }
    const index =
      p.call != null
        ? lines.findIndex((line) => line.progress.call === p.call && line.found === undefined)
        : lines.length - 1;
    if (index >= 0 && lines[index].found === undefined) {
      return lines.map((line, i) => (i === index ? {...line, found: p.message} : line));
    }
    return [...lines, {progress: p}];
  }, []);

/** A step's progress, e.g. Dash's searches, each with its queries and what it found. */
export const ToolProgressLines = ({lines}: {lines: ProgressLine[]}) => (
  <Stack gap="4">
    {lines.map(({progress: p, found}, i) => (
      <Stack key={i} gap="2">
        <Text size="xsmall" color="subtle">
          {p.message}
          {found ? ` · ${found}` : null}
        </Text>
        {p.searches?.map((s, j) => (
          <Text key={j} size="xsmall" color="subtle" style={{paddingLeft: 8, fontStyle: 'italic'}}>
            &ldquo;{s.query}&rdquo;
          </Text>
        ))}
      </Stack>
    ))}
  </Stack>
);

/**
 * What Dash searched and found, after its line once it's done: "Slack · 40
 * messages", or "55 results" when it only searched Dash itself. The line's own
 * label stays "Searching Dash" throughout; its searches show under it.
 */
export const dashSummary = (progress: AiQueryToolProgress[]): string | undefined => {
  const searched = progress.filter((p) => p.source);
  const apps = Array.from(new Set(searched.map((p) => p.source))).filter(
    (source) => source !== DASH_SOURCE
  );
  const sources = apps.length > 0 ? apps.join(', ') : undefined;
  const counts = searched.filter((p) => p.found != null);
  const units = new Set(counts.map((p) => p.unit));
  const [unit] = Array.from(units);
  const foundKey = `ai_query_dash_found_${unit}`;
  const total = counts.reduce((sum, p) => sum + (p.found ?? 0), 0);
  // Finding nothing isn't worth saying.
  const found =
    total > 0 && units.size === 1 && i18n.exists(`${foundKey}_other`)
      ? i18n.t(foundKey, {count: total})
      : undefined;
  return sources && found ? i18n.t('ai_query_dash_summary', {sources, found}) : (sources ?? found);
};

/** The sources Dash cited, from its one row (`DashAnswerDTO.sources`). */
export const dashSources = (items: Array<Record<string, unknown>> | undefined): DashSource[] => {
  const sources = items?.[0]?.sources;
  if (!Array.isArray(sources)) {
    return [];
  }
  return sources.flatMap((s) =>
    s && typeof s.title === 'string' && s.title.trim()
      ? [{title: sourceTitle(s.title), url: typeof s.url === 'string' && s.url ? s.url : null}]
      : []
  );
};

// Slack channel and DM ids the sub-agent copies into its link text, e.g. " (D0683730CBD)".
const SLACK_ID = /\s*\([CDG][A-Z0-9]{8,}\)/g;
// "2026-10-26 05:55:12 PT", with or without seconds and zone.
const DATE_TIME =
  /(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})(?::\d{2})?(?:\s*(?:PT|PDT|PST|UTC|Z)\b)?/g;
const DATE = /\d{4}-\d{2}-\d{2}/g;

/** "DM (D0683730CBD), 2026-10-26 05:55:12 PT" → "DM, Oct 26, 5:55 AM". */
export const sourceTitle = (title: string): string =>
  title
    .replace(SLACK_ID, '')
    .replace(DATE_TIME, (_match, day: string, hour: string, minute: string) => {
      const h = Number(hour);
      return `${formatShortDate(day)}, ${h % 12 || 12}:${minute} ${h < 12 ? 'AM' : 'PM'}`;
    })
    .replace(DATE, (day) => formatShortDate(day))
    .trim();

/** What Dash cited, as titled links, one per line. */
export const DashSources = ({sources}: {sources: DashSource[]}) => (
  <Stack gap="4">
    <Text size="xsmall" color="faint" tagName="div">
      {i18n.t('ai_query_dash_cited', {count: sources.length})}
    </Text>
    {sources.map((source, i) => (
      <Text key={i} size="xsmall" tagName="div">
        {source.url ? (
          <Link
            to={source.url}
            openInNewTab
            rel="noopener noreferrer"
            style={{fontSize: 'inherit'}}
          >
            {source.title}
          </Link>
        ) : (
          source.title
        )}
      </Text>
    ))}
  </Stack>
);
